import {
  NavigationStateService,
  pxToRem,
  ResizeHandler,
  SidenavService,
  SidenavToggleButton,
} from '@abb-procure/common';
import { PreferencesState } from '@abb-procure/data';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  output,
  viewChild,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';

@Component({
  selector: 'procure-app-secondary-navigation',
  templateUrl: './app-secondary-navigation.component.html',
  styleUrl: './app-secondary-navigation.component.scss',
  imports: [RouterOutlet, ResizeHandler, SidenavToggleButton],
  providers: [SidenavService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppSecondaryNavigation {
  readonly navElement = viewChild('sidenav', { read: ElementRef });
  readonly activated = output<void>();

  readonly sidenavService = inject(SidenavService);
  protected readonly preferences = inject(PreferencesState);
  private readonly navigationState = inject(NavigationStateService);

  protected resizingEvent = {
    isResizing: false,
    startingCursorX: 0,
    startingWidth: 0,
  };

  constructor() {
    this.sidenavService.setSideNavOptions({
      cssVarName: '--procure-secondary-navigation-width',
      placement: 'left',
      clampedMinWidth: 14,
      clampedMaxWidth: 30,
      defaultWidth: 16,
    });

    effect(() => {
      const preferredWidth = this.preferences.secondaryNavigationWidth();
      if (preferredWidth) {
        this.sidenavService.setSidenavWidth(preferredWidth);
      } else {
        this.sidenavService.setBaseWidth();
      }
    });

    effect(() => {
      if (
        this.preferences.isSecondaryNavigationExpanded() !==
        this.sidenavService.isExpanded()
      ) {
        this.preferences.setSecondaryNavigationWidth(
          this.sidenavService.width(),
        );
      }
    });
  }

  @HostListener('window:mousemove', ['$event'])
  updateSidenavWidth(event: MouseEvent): void {
    if (!this.resizingEvent.isResizing) {
      return;
    }
    this.sidenavService.resizeSidenav(
      this.resizingEvent.startingCursorX,
      event.clientX,
      this.resizingEvent.startingWidth,
    );
  }

  @HostListener('window:mouseup')
  stopResizing(): void {
    if (this.resizingEvent.isResizing) {
      this.preferences.setSecondaryNavigationWidth(this.sidenavService.width());
    }
    this.resizingEvent.isResizing = false;
  }

  protected startResizing(event: MouseEvent): void {
    const currentHostElementWidth =
      this.navElement()?.nativeElement.offsetWidth;
    this.resizingEvent = {
      isResizing: true,
      startingCursorX: pxToRem(event.clientX),
      startingWidth: pxToRem(currentHostElementWidth),
    };
  }

  protected toggle(): void {
    this.sidenavService.toggleExpansion();
    this.preferences.setSecondaryNavigationWidth(this.sidenavService.width());
  }

  protected onSecondaryActivate(): void {
    this.activated.emit();
    this.navigationState.setHasSecondaryAsideContent(true);
  }

  protected onSecondaryDeactivate(): void {
    this.navigationState.setHasSecondaryAsideContent(false);
  }
}
